import { Emojis } from 'constants/constants';
import { DateTimeUnitKind, SelectableUnit } from 'types/Schedule.types';

export const daysInMonthInLeapYears = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
export const daysInMonthsInNonLeapYears = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

export const dateTimeMonthSelectableUnits: SelectableUnit[] = [
  { value: 0, label: 'JAN', selected: false },
  { value: 1, label: 'FEB', selected: false },
  { value: 2, label: 'MAR', selected: false },
  { value: 3, label: 'APR', selected: false },
  { value: 4, label: 'MAY', selected: false },
  { value: 5, label: 'JUN', selected: false },
  { value: 6, label: 'JUL', selected: false },
  { value: 7, label: 'AUG', selected: false },
  { value: 8, label: 'SEP', selected: false },
  { value: 9, label: 'OCT', selected: false },
  { value: 10, label: 'NOV', selected: false },
  { value: 11, label: 'DEC', selected: false },
];

export const getYears = (selectedYear: number): SelectableUnit[] => {
  const currentYear = new Date().getFullYear();

  return [
    { value: currentYear, label: String(currentYear), selected: currentYear === selectedYear },
    { value: currentYear + 1, label: String(currentYear + 1), selected: currentYear + 1 === selectedYear },
    { value: currentYear + 2, label: String(currentYear + 2), selected: currentYear + 2 === selectedYear },
  ];
};

export const getMonths = (selectedMonth: number): SelectableUnit[] => {
  const dateTimeMonths = dateTimeMonthSelectableUnits.slice();
  dateTimeMonths.splice(selectedMonth, 1, { ...dateTimeMonthSelectableUnits[selectedMonth], selected: true });
  return dateTimeMonths;
};

/**
0  1  2  3  4  5  6
01 02 03 04 05 06 07
08 09 10 11 12 13 14
15 16 17 18 19 20 21
22 23 24 25 26 27 28
29 30 31
 */
export const getDates = (selectedDate: number, date: Date): SelectableUnit[] => {
  const daysInSelectedMonth = getMaxDate(date.getMonth(), date);
  const tempDate = new Date(date);

  tempDate.setDate(1);
  const numCellsToFill = tempDate.getDay();
  const fillerArray = [];

  for (let i = 0; i < numCellsToFill; i++) {
    fillerArray.push({
      value: -1,
      label: ' ',
      selected: false,
    });
  }

  const arrDates = Array.from(Array(daysInSelectedMonth + 1).keys()).slice(1);

  const calDates = arrDates.map((d) => {
    return {
      value: d,
      label: String(d),
      selected: d === selectedDate,
    };
  });

  return [...fillerArray, ...calDates];
};

export const getHours = (selectedHour: number): SelectableUnit[] => {
  const arrDates = Array.from(Array(12).keys());

  return arrDates.map((d) => ({
    value: d,
    label: String(d % 12 || 12).padStart(2, '0'),
    selected: d === selectedHour,
  }));
};

export const getMinutes = (selectedDate: number): SelectableUnit[] => {
  return [
    { value: 0, label: '00', selected: selectedDate === 0 },
    { value: 10, label: '10', selected: selectedDate === 10 },
    { value: 20, label: '20', selected: selectedDate === 20 },
    { value: 30, label: '30', selected: selectedDate === 30 },
    { value: 40, label: '40', selected: selectedDate === 40 },
    { value: 50, label: '50', selected: selectedDate === 50 },
  ];
};

export const getAmPm = (selected: number): SelectableUnit[] => {
  return [
    { value: 0, label: 'AM', selected: selected === 0 },
    { value: 1, label: 'PM', selected: selected === 1 },
  ];
};

export const getSelectableUnits = (value: number, kind: DateTimeUnitKind, dateObj: Date): SelectableUnit[] => {
  switch (kind) {
    case DateTimeUnitKind.Year:
      return getYears(value);
    case DateTimeUnitKind.Month:
      return getMonths(value);
    case DateTimeUnitKind.Date:
      return getDates(value, dateObj);
    case DateTimeUnitKind.Hours:
      return getHours(value);
    case DateTimeUnitKind.Minutes:
      return getMinutes(value);
    case DateTimeUnitKind.AmPm:
      return getAmPm(value);

    default:
      console.log(`${Emojis.eyes} Default case reached with %c ${kind} %c`);
      return [];
  }
};

export const getFormattedDateTimeUnit = (value: number, kind: DateTimeUnitKind): string => {
  switch (kind) {
    case DateTimeUnitKind.Year:
      return String(value);
    case DateTimeUnitKind.Month:
      return dateTimeMonthSelectableUnits[value].label;
    case DateTimeUnitKind.Date:
      return String(value);
    case DateTimeUnitKind.Hours:
      return String(value % 12 || 12);
    case DateTimeUnitKind.Minutes:
      return String(value).padStart(2, '0');
    case DateTimeUnitKind.AmPm:
      return value ? 'PM' : 'AM';

    default:
      console.log(`${Emojis.eyes} Default case reached with %c ${kind} %c`);
      return kind;
  }
};

export const isCenturionYear = (year: number): boolean => {
  return year % 100 === 0;
};

export const isLeapYear = (year: number): boolean => {
  if (isCenturionYear(year)) return year % 400 === 0;
  return year % 4 === 0;
};

export const getMaxDate = (month: number, date: Date): number => {
  if (isLeapYear(date.getFullYear())) return daysInMonthInLeapYears[month];

  return daysInMonthsInNonLeapYears[month];
};

export const getDefaultTimeTomorrow = () => {
  const curDate = new Date();

  curDate.setDate(curDate.getDate() + 1);
  curDate.setHours(10);
  curDate.setMinutes(0);
  curDate.setSeconds(0);
  curDate.setMilliseconds(0);

  return curDate;
};

export type DateTimeStrings = {
  timeString: string;
  dateString: string;
};
export const getDateAndTimeStrings = (dateStr: string): DateTimeStrings => {
  const dateObj = new Date(dateStr);
  const dateString = dateObj.toDateString();
  const timeString = dateObj.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: true });

  return { dateString, timeString };
};
